
import React, { useEffect, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { FaEye, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { Toast } from "primereact/toast";
import "../styles/merchants.css";
import { deleteTerminalByID, getAllMerchants } from "../api/api";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../utils/authStorage"; // ✅ Import local storage helper

const Terminals = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [terminals, setAllTerminals] = useState([]);
  const toast = React.useRef(null);

  // ✅ Fetch logged-in user
  const user = getUserData()?.user;
  const userType = user?.user_type;
  const userRoles = Array.isArray(user.roles)
    ? user.roles.flat() // handles nested arrays like [['view-only']]
    : [];


  const isAdmin = userType === "admin";
  const isViewOnly = userRoles.includes("view-only");
  const isTerminalCreation = userRoles.includes("terminal_creation");

  // ✅ Fetch all terminals
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllMerchants();
        if (res?.data) {
          const allTerminals = res.data.flatMap((merchant) =>
            merchant.terminals
              ? merchant.terminals.map((t) => ({
                  ...t,
                  merchantId: merchant.MID,
                  merchantName: merchant.MerchantName,
                }))
              : []
          );
          setAllTerminals(allTerminals);
        }
      } catch (err) {
        console.error("Error fetching merchants:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refreshTerminals = async () => {
    try {
      const res = await getAllMerchants();
      if (res?.data) {
        const allTerminals = res.data.flatMap((merchant) =>
          merchant.terminals
            ? merchant.terminals.map((t) => ({
                ...t,
                merchantId: merchant.MID,
                merchantName: merchant.MerchantName,
              }))
            : []
        );
        setAllTerminals(allTerminals);
      }
    } catch (err) {
      console.error("Error fetching merchants:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTerminal = async (tid) => {
    try {
      const res = await deleteTerminalByID(tid);
      if (res?.isSuccess) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Terminal deleted successfully",
          life: 2000,
        });
        refreshTerminals();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res?.message || "Failed to delete Terminal",
          life: 3000,
        });
      }
    } catch (err) {
      console.error("Error deleting Terminal:", err);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while deleting Terminal",
        life: 3000,
      });
    }
  };

  const columns = useMemo(
    () => [
      // { field: "_id", header: "ID", style: { width: "80px" } },
      { field: "MID", header: "MID", style: { width: "200px" } },
      { field: "TID", header: "TID", style: { width: "200px" } },
      {
        field: "PhoneNumber",
        header: "Phone Number",
        style: { width: "200px" },
      },
      {
        field: "merchantName",
        header: "Merchant Name",
        style: { width: "220px" },
      },
    ],
    []
  );

  // ✅ Conditional action buttons
  const actionBodyTemplate = (rowData) => (
    <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
      {/* Everyone can view */}
      <FaEye
        className="view-icon"
        title="View"
        onClick={() =>
          navigate("/dashboard/terminal?mode=view", {
            state: { terminal: rowData, mode: "view" },
          })
        }
      />

      {/* Only Admin or Terminal Creation role can edit/delete */}
      {(isAdmin || isTerminalCreation) && !isViewOnly && (
        <>
          <FaEdit
            title="Edit"
            className="view-icon"
            onClick={() =>
              navigate("/dashboard/terminal?mode=edit", {
                state: { terminal: rowData, mode: "edit" },
              })
            }
          />
          <FaTrash
            className="delete-icon"
            title="Delete"
            onClick={() => {
              const confirmed = window.confirm(
                `Are you sure you want to delete terminal "${rowData.TID}"?`
              );
              if (confirmed) handleDeleteTerminal(rowData.TID);
            }}
          />
        </>
      )}
    </div>
  );


 if (loading) {
    return (
      <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="db-loader"><div className="loader-spinner" /><p>Loading Terminals data…</p></div>
      </div>
    );
  }
  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
      <Toast ref={toast} />

      {/* ✅ Add Button only for Admin or Terminal Creation role */}
      {(isAdmin || isTerminalCreation) && !isViewOnly && (
        <div className="add-merchant-container">
          <button
            className="add-merchant-btn"
            onClick={() => navigate("/dashboard/terminal?mode=create")}
          >
            <FaPlus />
          </button>
        </div>
      )}

      <section className="reporting-table-section">
        <DataTable
          value={terminals}
          dataKey="msID"
          className="dashboard-table"
          responsiveLayout="scroll"
          paginator
          rows={10}
          paginatorClassName="custom-paginator"
        >
          {columns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              style={col.style}
            />
          ))}
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{ width: "180px" }}
          />
        </DataTable>
      </section>
    </div>
  );
};

export default Terminals;
